import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { ConvertToAMIState } from '../convertToAMIWizard';

const amiIdConfig = getCloudFieldConfig(Provider.Aws, 'ami');
const amiNameConfig = getCloudFieldConfig(Provider.Aws, 'amiName', {
  required: true,
});

export const ImageConfiguration = (props: StepProps<ConvertToAMIState>) => {
  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={<EonTagRemark />}
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      nextButtonText='Next'
      onBackClick={props.back}
      canGoNext={() => {
        if (
          !props.wizardState.regionName ||
          !props.wizardState.name ||
          !amiNameConfig.validation?.validateInput(props.wizardState.name)
        ) {
          return false;
        }

        if (
          props.wizardState.useCustomAMI &&
          (!props.wizardState.imageId ||
            !amiIdConfig.validation?.validateInput(props.wizardState.imageId))
        ) {
          return false;
        }

        return true;
      }}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Stack gap='24px'>
        <Box className='mx-[40px]'>
          <Stack
            direction='row'
            alignItems='center'
            gap='36px'
            className='mt-[20px]'
          >
            <RestoreRegionSelect
              isRequired
              cloudProvider={Provider.Aws}
              restoreAccountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              onChange={(regionName) =>
                props.setWizardState((state) => ({
                  ...state,
                  regionName: regionName,
                }))
              }
            />
            <FormTextField
              sx={{ flex: 1 }}
              title='Image name'
              dataTestId='image-name-field'
              placeholder={amiNameConfig.placeholder}
              value={props.wizardState.name || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  name: event.target.value,
                }))
              }
              validation={amiNameConfig.validation}
            />
          </Stack>
        </Box>
        <Box className='mx-[40px]'>
          <Stack direction='row' alignItems='center' gap='36px'>
            <FormControl size='small' sx={{ width: '50%' }}>
              <FormLabel>Image description (optional)</FormLabel>
              <TextField
                data-testid='image-description-field'
                size='small'
                value={props.wizardState.description || ''}
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    description: event.target.value,
                  }))
                }
              />
            </FormControl>
            <div style={{ width: '50%' }} />
          </Stack>
        </Box>
        <Divider />
        <AmiIdSection
          useCustomAMI={props.wizardState.useCustomAMI || false}
          setUseOriginalAMI={(value) =>
            props.setWizardState((state) => ({
              ...state,
              useCustomAMI: value,
            }))
          }
          amiId={props.wizardState.imageId || ''}
          setAmiId={(value) =>
            props.setWizardState((state) => ({ ...state, imageId: value }))
          }
        />
        <Divider />
        <TagsSection
          initialTags={props.initialState.tags || {}}
          tags={props.wizardState.tags || {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({
              ...state,
              tags: tags,
            }))
          }
        />
        <Divider />
      </Stack>
    </StepContainer>
  );
};

const AmiIdSection = ({
  useCustomAMI,
  setUseOriginalAMI,
  amiId,
  setAmiId,
}: {
  useCustomAMI: boolean;
  setUseOriginalAMI: (value: boolean) => void;
  amiId: string;
  setAmiId: (value: string) => void;
}) => {
  return (
    <Stack className='w-full px-[40px]' gap='24px'>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        className='group/allSecondaryIndexes'
      >
        <Typography>Use standard AMI settings</Typography>
        <Switch
          size='small'
          checked={!useCustomAMI}
          onChange={() => {
            setUseOriginalAMI(!useCustomAMI);
          }}
        />
      </Stack>
      {useCustomAMI && (
        <Stack direction='row' gap='36px' alignItems='center'>
          <FormTextField
            sx={{ width: '50%' }}
            title='Custom base AMI ID'
            placeholder={amiIdConfig.placeholder}
            value={amiId}
            onChange={(e) => setAmiId(e.target.value)}
            validation={amiIdConfig.validation}
          />
          <div style={{ width: '50%' }} />
        </Stack>
      )}
    </Stack>
  );
};
